import { io } from "socket.io-client";

// Registry of "join" emits to replay whenever the socket (re)connects, so a
// dropped connection or a server cold start transparently restores rooms and
// server-side state. One entry per event name: { params, onAck }.
const sessionJoins = {};

// Joins replay in a fixed order so re-registration never depends on object key
// insertion order (e.g. the personal room join lands before assignment joins).
const JOIN_REPLAY_ORDER = [
    'join',
    'joinedAdminRoom',
    'joinSharedScenario',
    'joinAssignmentRoom',
    'joinAsTeacher',
];

const ACK_TIMEOUT_MS = 8000;

// How long to wait for the server's `identity` frame before emitting joins
// anyway. Only reached against a server too old to send one; without this,
// classroom joins (which do not depend on handshake identity) would hang.
const IDENTITY_TIMEOUT_MS = 2000;

const socket = io(`${process.env.REACT_APP_API_URL_LOCAL}`, {
    reconnection: true,
    reconnectionDelay: 1000,
    reconnectionDelayMax: 5000,
    reconnectionAttempts: Infinity,
    transports: ['websocket'],
    // The server identifies the connection from the auth cookie on the handshake,
    // which the browser only attaches cross-origin when credentials are requested.
    withCredentials: true,
});

/*
 * --- Identity ---------------------------------------------------------------
 *
 * The server binds a connection to whoever the JWT cookie on the handshake says
 * it is, and refuses identity-scoped joins (personal notifications, the
 * collaboration room, the admin room) for connections it cannot identify.
 * Logging in and out are SPA transitions, so a socket opened while logged out
 * stays bound to that anonymous handshake — its joins would be silently refused
 * for the rest of the session.
 *
 * So the server reports the identity it derived, and when that disagrees with
 * the session the app believes it is in, we reconnect: a fresh handshake carries
 * the current cookie. One corrective attempt only — if the identity still does
 * not match, the cookie genuinely is not reaching the handshake and reconnecting
 * forever would just spin.
 *
 * CONTRACT: `setSocketIdentity` must be called once auth resolves, INCLUDING with
 * null for a signed-out visitor. AuthContext does this in all three of its
 * outcomes. It is what triggers the corrective reconnect, and therefore what
 * eventually releases any join held by `mayEmitJoin` below.
 */
let desiredUserId;      // undefined until the app resolves its auth state
let connectionUserId;   // undefined until the server reports for this connection
let identityRetried = false;
// Joins are held until identity is settled, so a join is never emitted onto a
// connection we are about to replace.
let joinsUnblocked = false;
let identityTimer = null;

const clearIdentityTimer = () => {
    if (identityTimer) {
        clearTimeout(identityTimer);
        identityTimer = null;
    }
};

/**
 * Whether a registered join may go out on the connection currently in front of us.
 *
 * Some joins are authorised by the server from the handshake identity rather than
 * from their payload (the personal notification room, the admin room, the
 * scenario collaboration room). Those must not be emitted on a connection bound
 * to somebody else — or to nobody.
 *
 * The check is against the identity the CALLER declared the join is for, not
 * against this module's own view of the session, because this module is the last
 * to find out. React runs child effects before parent ones, so NotificationContext
 * registers the personal-room join before AuthContext calls `setSocketIdentity`;
 * at that instant `desiredUserId` and `connectionUserId` still both hold the old
 * signed-out value and agree with each other perfectly. They are agreeing about
 * the wrong session. The caller passing `forUserId` is the only party that knows.
 *
 * A join with no `forUserId` carries its own user id in its payload (the classroom
 * ones) and is unaffected.
 */
const mayEmitJoin = (eventName) => {
    const forUserId = sessionJoins[eventName]?.forUserId;
    return forUserId === undefined || connectionUserId === forUserId;
};

const emitJoin = (eventName, { params, onAck }) => {
    if (onAck) {
        // onAck is called as (err, response): err is set on ack timeout.
        socket.timeout(ACK_TIMEOUT_MS).emit(eventName, params, onAck);
    } else {
        socket.emit(eventName, params);
    }
};

const emitAllJoins = () => {
    const remaining = { ...sessionJoins };
    for (const eventName of JOIN_REPLAY_ORDER) {
        if (remaining[eventName]) {
            if (mayEmitJoin(eventName)) emitJoin(eventName, remaining[eventName]);
            delete remaining[eventName];
        }
    }
    for (const [eventName, entry] of Object.entries(remaining)) {
        if (mayEmitJoin(eventName)) emitJoin(eventName, entry);
    }
};

const unblockJoins = () => {
    clearIdentityTimer();
    joinsUnblocked = true;
    emitAllJoins();
};

const reconnectForIdentity = () => {
    clearIdentityTimer();
    joinsUnblocked = false;
    connectionUserId = undefined;
    if (socket.connected) socket.disconnect();
    socket.connect();
};

socket.on("connect", () => {
    joinsUnblocked = false;
    connectionUserId = undefined;
    clearIdentityTimer();
    identityTimer = setTimeout(unblockJoins, IDENTITY_TIMEOUT_MS);
});

socket.on('identity', ({ userId = null } = {}) => {
    connectionUserId = userId;

    // Release the connection either way. When auth has not resolved yet this only
    // lets the joins that never depended on handshake identity through — the
    // identity-scoped ones stay held by `mayEmitJoin` until setSocketIdentity
    // confirms this is the right connection, or replaces it.
    if (desiredUserId === undefined || connectionUserId === desiredUserId) {
        identityRetried = false;
        unblockJoins();
        return;
    }

    if (!identityRetried) {
        identityRetried = true;
        reconnectForIdentity();
        return;
    }

    // Second failure: the cookie is not reaching the handshake at all (a
    // SameSite/secure or proxy problem, not a stale connection). Let the joins
    // through so everything that does not depend on handshake identity keeps
    // working, and say plainly what is broken.
    console.error(
        `Socket identity mismatch: signed in as ${desiredUserId} but the server sees ` +
        `${connectionUserId === null ? 'an anonymous connection' : connectionUserId}. ` +
        'The auth cookie is not reaching the WebSocket handshake, so notifications ' +
        'and scenario collaboration will not work.'
    );
    unblockJoins();
});

/**
 * Tell the socket who the app is signed in as (null when signed out). Call this
 * whenever auth state settles or changes; a mismatch with the current
 * connection's identity triggers one corrective reconnect.
 */
export const setSocketIdentity = (userId) => {
    const next = userId || null;
    if (next === desiredUserId) return;

    desiredUserId = next;
    identityRetried = false;

    if (connectionUserId !== undefined && connectionUserId !== desiredUserId) {
        identityRetried = true;
        reconnectForIdentity();
    }
};

socket.on('disconnect', (reason) => {
    joinsUnblocked = false;
    connectionUserId = undefined;
    clearIdentityTimer();
    console.warn(`Socket disconnected: ${reason}`);
});

socket.on("connect_error", (err) => {
    console.error("Socket connection error:", err);
});

/**
 * Register a join event to be (re)emitted on every connect, once the server has
 * confirmed the connection's identity. Optional onAck receives (err, response) —
 * err is non-null if the server didn't ack within the timeout (e.g. mid cold
 * start); the join replays on the next connect.
 *
 * Pass `forUserId` for any join the server authorises from the handshake identity
 * (`join`, `joinedAdminRoom`, `joinSharedScenario`). It is held until the
 * connection is actually bound to that user, so it can never be emitted onto a
 * socket opened before the user signed in — which the server would refuse.
 *
 * @param {string} eventName
 * @param {any} params
 * @param {Function} [onAck]
 * @param {{forUserId?: string}} [options]
 */
export const addJoinEvent = (eventName, params, onAck, { forUserId } = {}) => {
    sessionJoins[eventName] = { params, onAck, forUserId };
    if (socket.connected && joinsUnblocked && mayEmitJoin(eventName)) {
        emitJoin(eventName, sessionJoins[eventName]);
    }
};

/**
 * Merge fresh fields into a registered join's params WITHOUT re-emitting it.
 * Used to keep replay payloads current (e.g. the student's latest situationId)
 * so a reconnect after a server cold start restores up-to-date state.
 */
export const updateJoinParams = (eventName, partialParams) => {
    const entry = sessionJoins[eventName];
    if (entry) {
        entry.params = { ...entry.params, ...partialParams };
    }
};

export const removeJoinEvent = (eventName) => {
    delete sessionJoins[eventName];
};

export default socket;
